import type { PolygonOpenContour } from '../polygon-open-contour';

import type { PointArray } from '../types/contour-definition-array-type';

import { createLaxExtendLineFromPolygon } from './createLaxExtendLine';
import { createLaxTransmuralityPolygons } from './createLaxTransmuralityPolygons';
import { getClosestPointIdx } from './getClosestPointIdx';

function interpolateOnArc(
  centerline: PointArray,
  middleIndex: number,
  arcLengths: number[],
  targetLength: number,
  direction: number,
): { x: number; y: number } {
  let lo = 0;
  while (lo < arcLengths.length - 1 && arcLengths[lo + 1] < targetLength) {
    lo++;
  }
  const hi = Math.min(lo + 1, arcLengths.length - 1);
  const denom = arcLengths[hi] - arcLengths[lo];
  const t = denom > 0 ? (targetLength - arcLengths[lo]) / denom : 0;

  const idxLo = middleIndex + direction * lo;
  const idxHi = middleIndex + direction * hi;
  return {
    x: centerline[idxLo].x + t * (centerline[idxHi].x - centerline[idxLo].x),
    y: centerline[idxLo].y + t * (centerline[idxHi].y - centerline[idxLo].y),
  };
}

function generateEqualArcLengthPointsOpen(
  centerline: PointArray,
  middleIndex: number,
  segments: number,
  shiftStartByHalfSegment: boolean = false,
): PointArray {
  if (centerline.length < 3 || segments <= 1) return [centerline[middleIndex]];

  // Apex always gets a chord; distribute the rest on both sides
  // For even total: left gets one more
  const remaining = segments - 1;
  const leftCount = Math.ceil(remaining / 2);
  const rightCount = Math.floor(remaining / 2);

  const leftArcLengths = [0];
  for (let i = middleIndex; i > 0; i--) {
    const dx = centerline[i].x - centerline[i - 1].x;
    const dy = centerline[i].y - centerline[i - 1].y;
    leftArcLengths.push(
      leftArcLengths[leftArcLengths.length - 1] + Math.sqrt(dx * dx + dy * dy),
    );
  }

  const rightArcLengths = [0];
  for (let i = middleIndex; i < centerline.length - 1; i++) {
    const dx = centerline[i + 1].x - centerline[i].x;
    const dy = centerline[i + 1].y - centerline[i].y;
    rightArcLengths.push(
      rightArcLengths[rightArcLengths.length - 1] + Math.sqrt(dx * dx + dy * dy),
    );
  }

  const leftTotalLength = leftArcLengths[leftArcLengths.length - 1];
  const rightTotalLength = rightArcLengths[rightArcLengths.length - 1];

  const leftStep = leftTotalLength / (leftCount + 1);
  const rightStep = rightTotalLength / (rightCount + 1);

  // apex chord stays fixed; only the side chords are nudged by half a step
  const leftOffset = shiftStartByHalfSegment ? leftStep / 2 : 0;
  const rightOffset = shiftStartByHalfSegment ? rightStep / 2 : 0;

  const result: PointArray = [];

  // Left side
  for (let i = leftCount; i >= 1; i--) {
    const targetLength = i * leftStep + leftOffset;
    result.unshift(
      interpolateOnArc(
        centerline,
        middleIndex,
        leftArcLengths,
        targetLength,
        -1,
      ),
    );
  }

  // Apical point (always included)
  result.push(centerline[middleIndex]);

  // Right side
  for (let i = 1; i <= rightCount; i++) {
    const targetLength = i * rightStep + rightOffset;
    result.push(
      interpolateOnArc(
        centerline,
        middleIndex,
        rightArcLengths,
        targetLength,
        1,
      ),
    );
  }

  return result;
}

function smoothUnitDirs(dirs: PointArray, radius: number): PointArray {
  const n = dirs.length;
  const out: PointArray = [];
  for (let i = 0; i < n; i++) {
    let sx = 0;
    let sy = 0;
    for (let k = -radius; k <= radius; k++) {
      const j = i + k;
      if (j < 0 || j >= n) continue;
      const L = Math.hypot(dirs[j].x, dirs[j].y) || 1;
      sx += dirs[j].x / L;
      sy += dirs[j].y / L;
    }
    const L = Math.hypot(sx, sy) || 1;
    out.push({ x: sx / L, y: sy / L }); // was: out[i] = ...
  }
  return out;
}

// nearest crossing of the full line through `origin` with dir (nx,ny)
function crossingAlongDir(
  origin: { x: number; y: number },
  nx: number,
  ny: number,
  contour: PointArray,
): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bestAbsS = Infinity;
  for (let i = 0; i < contour.length - 1; i++) {
    const a = contour[i];
    const b = contour[i + 1];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const det = nx * -ey - -ex * ny;
    if (Math.abs(det) < 1e-9) continue;
    const rx = a.x - origin.x;
    const ry = a.y - origin.y;
    const s = (rx * -ey - -ex * ry) / det;
    const u = (nx * ry - ny * rx) / det;
    if (u < 0 || u > 1) continue;
    if (Math.abs(s) < bestAbsS) {
      bestAbsS = Math.abs(s);
      best = { x: origin.x + s * nx, y: origin.y + s * ny };
    }
  }
  return best;
}

export function createLaxChordLines(
  endoContour: PolygonOpenContour,
  epiContour: PolygonOpenContour,
  segments: number = 6,
  shiftStartByHalfSegment: boolean = false,
): PointArray[] {
  const endo = endoContour.getPolygon();
  const epi = epiContour.getPolygon();

  const centerline = createLaxTransmuralityPolygons(
    endoContour,
    epiContour,
  )[10];
  const { apicalPointIdx } = createLaxExtendLineFromPolygon(centerline);

  const equalAnglePoints = generateEqualArcLengthPointsOpen(
    centerline,
    apicalPointIdx,
    segments,
    shiftStartByHalfSegment,
  );

  const endoClosest: PointArray = epi.map(e => endo[getClosestPointIdx(e, endo)]);
  const rawDirs: PointArray = epi.map((e, i) => ({
    x: e.x - endoClosest[i].x,
    y: e.y - endoClosest[i].y,
  }));
  const dirs = smoothUnitDirs(rawDirs, 6); // tune radius
  const mids: PointArray = epi.map((e, i) => ({
    x: 0.5 * (e.x + endoClosest[i].x),
    y: 0.5 * (e.y + endoClosest[i].y),
  }));

  const chordLines: PointArray[] = [];
  for (let s = 0; s < equalAnglePoints.length; s++) {
    const p = equalAnglePoints[s];
    // nearest dense index to this sample, to fetch its smoothed direction
    let j = 0;
    let bestD = Infinity;
    for (let k = 0; k < mids.length; k++) {
      const d = (mids[k].x - p.x) ** 2 + (mids[k].y - p.y) ** 2;
      if (d < bestD) {
        bestD = d;
        j = k;
      }
    }
    const { x: nx, y: ny } = dirs[j];
    const endoHit = crossingAlongDir(p, nx, ny, endo);
    const epiHit = crossingAlongDir(p, nx, ny, epi);
    if (endoHit && epiHit) chordLines.push([endoHit, epiHit]);
  }
  return chordLines;
}
